import PropTypes from 'prop-types';
import React, {ReactNode} from 'react';

import {GibsonParameters, GoldenGateParameters} from 'assembly/AssemblyModels';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import Enzymes from 'molbio/enzymes/backbone/Enzymes';

const COMMON_ENZYMES = Enzymes.getCommonEnzymes();

type AssemblyParameters<TAssembly extends ASSEMBLY_TYPE.GIBSON | ASSEMBLY_TYPE.GOLDEN_GATE> =
  TAssembly extends ASSEMBLY_TYPE.GIBSON ? GibsonParameters : GoldenGateParameters;

export interface DesignParametersProps<TAssembly extends ASSEMBLY_TYPE.GIBSON | ASSEMBLY_TYPE.GOLDEN_GATE> {
  assemblyType: TAssembly;
  parameters: AssemblyParameters<TAssembly>;
}

export class DesignParameters<
  TAssembly extends ASSEMBLY_TYPE.GIBSON | ASSEMBLY_TYPE.GOLDEN_GATE,
> extends React.Component<DesignParametersProps<TAssembly>> {
  static propTypes = {
    assemblyType: PropTypes.string.isRequired,
    parameters: PropTypes.object.isRequired,
  };

  renderParameter = (
    key: keyof AssemblyParameters<TAssembly>,
    value: AssemblyParameters<TAssembly>[keyof AssemblyParameters<TAssembly>]
  ): [ReactNode, ReactNode] => {
    switch (key) {
      case 'tmAnneal':
      case 'bindingRegionMinTM':
        return [
          <span>
            Min T<sub>m</sub>
            {" for primer's binding bases"}
          </span>,
          `${value} \u00B0C`,
        ];
      case 'tmWhole':
        return [
          <span>
            Min T<sub>m</sub>
            {' for whole primer'}
          </span>,
          `${value} \u00B0C`,
        ];
      case 'tmDiff':
      case 'primerPairMaxTMDiff':
        return [
          <span>
            Max T<sub>m</sub>
            {' diff for primer pairs'}
          </span>,
          `${value} \u00B0C`,
        ];
      case 'minOverlap':
        return ['Min length of homology/binding regions', value];
      case 'maxOverlap':
        return ['Max length of homology/binding regions', value];
      case 'preRecognitionSiteLength':
        return ['Pre-recognition site length', value];
      case 'preRecognitionSiteBases':
        return ['Pre-recognition site bases', value || '(random)'];
      case 'bindingRegionMinLength':
        return ['Min length for binding region', value];
      case 'enzymeID':
        // @ts-expect-error .get can't handle the type of value, so bypassing this.  Added during typescript 5.7.3 upgrade.
        return ['Type IIS Enzyme', COMMON_ENZYMES.get(value).name()];
      default:
        // @ts-expect-error Implicit conversion of symbol -> string will crash; fortunately, all the keys
        // are stringy
        throw new Error(`Unexpected parameter key: ${key}`);
    }
  };

  override render(): JSX.Element {
    let orderedKeys: Array<keyof AssemblyParameters<TAssembly>>;
    if (this.props.assemblyType === ASSEMBLY_TYPE.GIBSON) {
      orderedKeys = ['tmAnneal', 'tmWhole', 'tmDiff', 'minOverlap', 'maxOverlap'] as Array<
        keyof AssemblyParameters<TAssembly>
      >;
    } else {
      orderedKeys = [
        'preRecognitionSiteLength',
        'preRecognitionSiteBases',
        'bindingRegionMinTM',
        'bindingRegionMinLength',
        'primerPairMaxTMDiff',
        'enzymeID',
      ] as Array<keyof AssemblyParameters<TAssembly>>;
    }
    return (
      <fieldset>
        <legend>Design Parameters</legend>
        <table className="table table-condensed table-hover">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {orderedKeys.map((key) => {
              const [renderedKey, renderedValue] = this.renderParameter(key, this.props.parameters[key]);
              return (
                <tr key={key as string}>
                  <td>{renderedKey}</td>
                  <td>{renderedValue}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </fieldset>
    );
  }
}
